import {
  type ClientOptions,
  type ProfileKey,
  sapi,
  type SapiClient,
  type SearchParameters
} from '@findhotel/sapi'
import {waitUntil} from '@vercel/functions'
import {preloadPricePrediction} from 'components/PriceTrend/services/preload'
import {priceInsightsApi} from 'components/PriceTrend/services/priceInsightsApi'
import {REVIEWS_PAGE_SIZE} from 'components/Reviews/ReviewsList/ReviewsList'
import {setAccommodationHotelId} from 'modules/accommodation/slice'
import {
  getAnonymousId,
  getLanguageCode,
  getLocaleCode
} from 'modules/meta/selectors'
import {getIsNoDatesSearch} from 'modules/search/selectors'
import {
  preheatRooms,
  preloadFAQ,
  preloadHotel,
  preloadHotelRooms,
  preloadReviews
} from 'modules/searchApi/preload'
import {getServerPreheatRoomsParams} from 'modules/searchApi/selectors'
import {seoApi} from 'modules/seoApi'
import {
  preloadSeoBreadcrumb,
  preloadSeoHotelAverageRate,
  preloadSeoHotelDescription,
  preloadSeoHotelMetadata,
  preloadSeoHotelPriceInsightsFAQ,
  preloadSeoTopHotels
} from 'modules/seoApi/preload'
import {COUNTRY_TOP_HOTELS_LIMIT} from 'modules/seoApi/selectors'
import {translateInsightsApi} from 'modules/translateInsightsApi'
import {preloadAiPoweredInsights} from 'modules/translateInsightsApi/preload'
import {
  preloadTrustpilotBusinessUnit,
  shouldPreloadTrustpilot
} from 'modules/trustpilot/preload'
import {ErrorProps} from 'next/error'
import {mapObjIndexed, pick, pipe} from 'ramda'
import {initializeStore, type RootState, Store} from 'store'
import {ApPathParams} from 'utils/server/apPathParams'
import {buildSeoApPath} from 'utils/server/apSeo/getSeoAPPath'
import {
  baseGetServerSideProps,
  CommonPageProps
} from 'utils/server/defaultGetServerSideProps'
import {getExperimentSegmentationAttributes} from 'utils/server/getExperimentSegmentationAttributes'
import {getServerSapiClientOptions} from 'utils/server/getServerSapiClientOptions'
import {
  hasPreloadedReduxState,
  isClientSideNavigation,
  isRedirectOrNotFound
} from 'utils/server/getServerSidePropsUtils'
import {getStatusCodeFromError} from 'utils/server/getStatusCodeFromError'
import {getUserAgentFromServerContext} from 'utils/server/getUserAgentFromServerContext'
import {logPreloadError} from 'utils/server/logPreloadError'
import {incrementMetric} from 'utils/server/metrics'
import {preloadBaseSearchState} from 'utils/server/preloadBaseSearchState'
import {getUrlObjectFromContext} from 'utils/server/searchParameters/utils/generic'
import {serverSideTimeouts} from 'utils/server/serverSideTimeouts'
import {getSrpSearchFlow, getTrafficSource} from 'utils/trafficSource'
import AccommodationView from 'views/accommodation'
import {convertHotelSlugToHotelId} from 'views/accommodation/convertHotelSlugToHotelId'

import {getBrand} from '@vio/core/src/_web/brand/modules/selectors'
import {createServerToggle} from '@vio/core/src/_web/experiments/createServerToggle'
import {Page} from '@vio/core/src/analytics/types/Events'
import {searchApi} from '@vio/core/src/sapi/services/searchApi'
import {trustpilotApi} from '@vio/core/src/trustpilotWidget/services/trustpilotApi'
import {removeEmptyRecursive} from '@vio/core/src/utils/object'
import {isTimeoutError} from '@vio/core/src/utils/promise/withTimeout'
import {parseQueryString} from '@vio/core/src/utils/url'
import {getSimulationFlag} from '@vio/debug-panel/src/utils/scenarioSimulator'

import type {GetServerSideProps} from 'next'

export interface PageProps extends CommonPageProps {
  searchParameters?: SearchParameters
  sapiClientOptions?: ClientOptions
  userIp?: string
  isEligibleForSSR?: boolean
  /**
   * The rooms preheat params the server built. The client compares them with its own to
   * find out what the server logic misses, and skips a duplicate preheat on the B side.
   */
  serverPreheatRoomsParams?: NonNullable<
    ReturnType<typeof getServerPreheatRoomsParams>
  >
}

/**
 * Select only the slices we want to provide and remove undefined values for serialization
 */
const sanitizeState = pipe<
  [RootState],
  DeepPartial<RootState>,
  DeepPartial<RootState>
>(
  pick([
    'meta',
    'accommodation',
    'experiments',
    'sapiSearch',
    'search',
    'searchBox',
    'seoApi',
    'searchApi',
    'priceInsightsApi',
    'translateInsightsApi',
    'trustpilotApi'
  ]),
  mapObjIndexed(removeEmptyRecursive)
)

export default AccommodationView

export const getServerSideProps: GetServerSideProps<
  PageProps | ErrorProps
> = async context => {
  const {
    params: pathParams,
    req: {cookies, url}
  } = context

  // Redirect lowercase /hotel/ to /Hotel/ (rewrite makes route accessible, this enforces canonical URL)
  if (url?.startsWith('/hotel/')) {
    return {
      redirect: {
        destination: url.replace(/^\/hotel\//, '/Hotel/'),
        permanent: true
      }
    }
  }

  // Validate path params before calling any downstream service. An invalid
  // hotelId or locale can only ever 404, so bail out early.
  if (!ApPathParams.safeParse(pathParams).success) {
    return {notFound: true}
  }

  const result = await baseGetServerSideProps({
    page: Page.Accommodation,
    isCacheable: false
  })(context)

  if (
    isRedirectOrNotFound(result) ||
    isClientSideNavigation(context) ||
    !hasPreloadedReduxState(result.props)
  ) {
    return result
  }

  const state = result.props.preloadedReduxState as RootState
  const brand = getBrand(state)
  const urlObject = getUrlObjectFromContext(context, brand)
  const queryParams = urlObject.searchParams
  const searchParameters = parseQueryString(queryParams.toString(), true)

  // No anonymousId = cached page, we cannot run toggle or dynamic SSR
  const anonymousId = getAnonymousId(state)
  if (!anonymousId) return result

  const {experiments} = result.props
  const attributes = getExperimentSegmentationAttributes(state, urlObject)
  const toggle = createServerToggle(anonymousId, attributes, experiments)

  const {srpSearchFlow, utm_source, label} = searchParameters
  const trafficSource = getTrafficSource(utm_source as string, label as string)
  const seoFlow = getSrpSearchFlow(
    brand,
    trafficSource,
    srpSearchFlow as string
  )

  const isSeoAP = seoFlow === 'seo'

  const metricTags = {
    brand: brand.code,
    locale: getLocaleCode(state),
    is_new_ap: 'true', // Legacy, ensure this keeps query working: https://github.com/FindHotel/daedalus/blob/master/packages/datadog/src/search/monitors/ssrAp.tf#L11
    is_seo: String(isSeoAP)
  }

  /**
   * Report a failed preload through the single path every preload uses.
   *
   * The metric always fires, since the anomaly monitor on ssr.ap.preload_error is what
   * alerting reads. The log is skipped for timeouts, which preloads provoke themselves
   * when they bail out to client-side rendering.
   * @param preloadName - Name of the preload, used as the metric's `preload` tag
   * @param error - The rejection or API error the preload produced
   */
  const reportPreloadFailure = (preloadName: string, error: unknown) => {
    logPreloadError(`Error preloading ${preloadName}:`, error)
    incrementMetric('ssr.ap.preload_error', {
      ...metricTags,
      preload: preloadName
    })
  }

  let store: Store
  let serverPreheatRoomsParams: PageProps['serverPreheatRoomsParams']
  try {
    // We create a single SAPI client (if we need one), consumers can await the promise to get it
    let sapiClientPromise: Promise<SapiClient>
    const getSapiClient = () => {
      if (!sapiClientPromise) {
        sapiClientPromise = sapi(
          process.env.NEXT_PUBLIC_SAPI_PROFILE_KEY as ProfileKey,
          {
            ...getServerSapiClientOptions(store.getState(), toggle, isSeoAP),
            userAgent: getUserAgentFromServerContext(context)
          }
        )
      }
      return serverSideTimeouts.sapiClient(sapiClientPromise)
    }

    store = initializeStore(state, {toggle, getSapiClient})

    await serverSideTimeouts.baseState(
      preloadBaseSearchState({
        store,
        context,
        pageName: Page.Accommodation
      })
    )

    const hasSimulationFlag = getSimulationFlag(queryParams, cookies)
    if (!hasSimulationFlag) {
      // Metric to see the total amount of request
      incrementMetric('ssr.ap.request', metricTags)

      const hotelId = convertHotelSlugToHotelId(pathParams.hotelSlug as string)
      const currentState = store.getState()

      // Built for everyone as a canary, only sent on the B side of the experiment
      serverPreheatRoomsParams = getServerPreheatRoomsParams(
        currentState,
        hotelId
      )
      if (
        serverPreheatRoomsParams &&
        toggle('feature-ssr-rooms-preheat', false, true)
      ) {
        // waitUntil keeps the function alive for the request after the response is sent
        waitUntil(
          serverSideTimeouts
            .roomsPreheat(
              store.dispatch(preheatRooms(serverPreheatRoomsParams))
            )
            .catch(error => {
              // The request is already sent. A slow answer isn't an SSR error, so it
              // stays out of the ssr.ap.preload_error guardrail.
              if (isTimeoutError(error)) {
                incrementMetric('ssr.ap.rooms_preheat_timeout', metricTags)
                return
              }
              reportPreloadFailure('roomsPreheat', error)
            })
        )
      }

      const languageCode = getLanguageCode(currentState)
      const shouldPreloadTopHotels = isSeoAP && getIsNoDatesSearch(currentState)
      const shouldPreloadHotelDescription =
        isSeoAP && toggle('feature-seo-hotel-descriptions', false, true)

      // Optional preloads - failures are logged but don't break SSR
      const optionalPreloads = {
        reviews: serverSideTimeouts.reviewsPreload(
          store.dispatch(
            preloadReviews({
              hotelId,
              language: languageCode,
              pageSize: REVIEWS_PAGE_SIZE
            })
          )
        ),
        ...(isSeoAP
          ? {
              hotelRooms: serverSideTimeouts.hotelRoomsPreload(
                store.dispatch(preloadHotelRooms({hotelId}))
              )
            }
          : {}),
        aiPoweredInsights: serverSideTimeouts.aiPoweredInsightsPreload(
          store.dispatch(
            preloadAiPoweredInsights({hotelId, lang: languageCode})
          )
        ),
        // SAPI FAQs are only supported for EN languages
        ...(languageCode.includes('en')
          ? {
              faq: serverSideTimeouts.faqPreload(
                store.dispatch(preloadFAQ({hotelId}))
              )
            }
          : {}),
        ...(shouldPreloadTrustpilot(currentState)
          ? {
              trustpilot: serverSideTimeouts.trustpilotPreload(
                store.dispatch(preloadTrustpilotBusinessUnit())
              )
            }
          : {})
      }

      const preloadEntries = Object.entries(optionalPreloads)
      const hotelPreload = serverSideTimeouts.hotelSearch(
        store.dispatch(preloadHotel({hotelId}))
      )
      // Chain the breadcrumb preload after the hotel preload since it depends on the hotel data.
      const breadcrumbPreload = hotelPreload.then(hotel => {
        const placeId = hotel?.data?.placeID

        if (!isSeoAP || !placeId) return

        return serverSideTimeouts.seoPreload(
          Promise.all([store.dispatch(preloadSeoBreadcrumb(placeId))])
        )
      })

      // Chain top hotels preload after the hotel preload since it depends on the hotel place data.
      const topHotelsPreload = hotelPreload.then(hotel => {
        if (!shouldPreloadTopHotels) return

        const placeId = hotel?.data?.placeID
        const countryPlaceId = hotel?.data?.navPathInfo?.country?.id

        if (!placeId && !countryPlaceId) return

        return serverSideTimeouts.seoPreload(
          Promise.all([
            placeId && store.dispatch(preloadSeoTopHotels(placeId)),
            countryPlaceId &&
              countryPlaceId !== placeId &&
              store.dispatch(
                preloadSeoTopHotels(countryPlaceId, COUNTRY_TOP_HOTELS_LIMIT)
              )
          ])
        )
      })

      const hotelMetadataPreload = isSeoAP
        ? serverSideTimeouts.seoPreload(
            Promise.all([store.dispatch(preloadSeoHotelMetadata())])
          )
        : Promise.resolve()

      const pricePredictionPreload = isSeoAP
        ? serverSideTimeouts.seoPreload(
            Promise.all([store.dispatch(preloadPricePrediction(hotelId))])
          )
        : Promise.resolve()

      const priceInsightsFAQPreload = isSeoAP
        ? serverSideTimeouts.seoPreload(
            Promise.all([
              store.dispatch(preloadSeoHotelPriceInsightsFAQ(hotelId))
            ])
          )
        : Promise.resolve()

      const averageRatePreload = isSeoAP
        ? serverSideTimeouts.seoPreload(
            Promise.all([store.dispatch(preloadSeoHotelAverageRate(hotelId))])
          )
        : Promise.resolve()

      const hotelDescriptionPreload = shouldPreloadHotelDescription
        ? serverSideTimeouts.seoPreload(
            Promise.all([store.dispatch(preloadSeoHotelDescription(hotelId))])
          )
        : Promise.resolve()

      const [
        hotelResult,
        breadcrumbResult,
        topHotelsResult,
        hotelMetadataResult,
        pricePredictionResult,
        priceInsightsFAQResult,
        averageRateResult,
        hotelDescriptionResult,
        ...optionalResults
      ] = await Promise.allSettled([
        hotelPreload,
        breadcrumbPreload,
        topHotelsPreload,
        hotelMetadataPreload,
        pricePredictionPreload,
        priceInsightsFAQPreload,
        averageRatePreload,
        hotelDescriptionPreload,
        ...preloadEntries.map(([, promise]) => promise)
      ])

      // Log optional preload failures without breaking SSR
      optionalResults.forEach((result, index) => {
        const isPromiseRejected = result.status === 'rejected'
        const isApiError = !isPromiseRejected && result.value.isError
        const preloadName = preloadEntries[index][0]

        if (isPromiseRejected) {
          reportPreloadFailure(preloadName, result.reason)
        }

        if (isApiError) {
          const {error} = result.value
          const statusCode = getStatusCodeFromError(error)

          // Report only errors
          if (statusCode !== '404') {
            reportPreloadFailure(preloadName, error)
          }
        }
      })

      if (hotelResult.status === 'rejected') {
        throw hotelResult.reason
      }

      if (hotelResult.value.isError) {
        const {error} = hotelResult.value
        const statusCode = getStatusCodeFromError(error)

        if (statusCode === '404') {
          incrementMetric('ssr.ap.404', metricTags)
          return {notFound: true}
        }

        if (statusCode && statusCode.startsWith('5')) {
          incrementMetric('ssr.ap.500', metricTags)
        }

        throw error
      }

      if (breadcrumbResult.status === 'rejected') {
        reportPreloadFailure('seoBreadcrumb', breadcrumbResult.reason)
      }

      if (topHotelsResult.status === 'rejected') {
        reportPreloadFailure('seoTopHotels', topHotelsResult.reason)
      }

      if (hotelMetadataResult.status === 'rejected') {
        reportPreloadFailure('seoHotelMetadata', hotelMetadataResult.reason)
      }

      if (pricePredictionResult.status === 'rejected') {
        reportPreloadFailure('pricePrediction', pricePredictionResult.reason)
      }

      if (priceInsightsFAQResult.status === 'rejected') {
        reportPreloadFailure('priceInsightsFAQ', priceInsightsFAQResult.reason)
      }

      if (averageRateResult.status === 'rejected') {
        reportPreloadFailure('averageRate', averageRateResult.reason)
      }

      if (hotelDescriptionResult.status === 'rejected') {
        reportPreloadFailure(
          'seoHotelDescription',
          hotelDescriptionResult.reason
        )
      }

      incrementMetric('ssr.ap.completed', metricTags)
    }

    // Double check AP URL in case landing in the the Seo AP (handle mismatched path and query param for locale)
    const {lang, forceLanguageChange} = searchParameters
    const locale = forceLanguageChange ? lang : ''

    const pathArgs = {
      hotelId: String(pathParams?.hotelSlug),
      locale: String(locale || pathParams?.locale)
    }

    store.dispatch(
      setAccommodationHotelId(
        convertHotelSlugToHotelId(pathParams.hotelSlug as string)
      )
    )

    const correctPath = buildSeoApPath(pathArgs)

    if (correctPath !== urlObject.pathname) {
      // sanitize query params
      queryParams.delete('lang')
      queryParams.delete('forceLanguageChange')

      const remainingParams = queryParams.toString()

      // Check if we have any more query params and then append it to the url
      const destination = remainingParams
        ? `${correctPath}?${queryParams.toString()}`
        : correctPath

      return {
        redirect: {
          destination: destination,
          permanent: false // 307 redirect
        }
      }
    }

    result.props.preloadedReduxState = sanitizeState(store.getState())
  } catch (error) {
    // Unhandled errors log & fall through to client-side search
    logPreloadError('Error preloading accommodation page:', error)
    incrementMetric('ssr.ap.csr_fallback', metricTags)

    // Reset to avoid sending partial SSR state
    result.props.preloadedReduxState = state
  } finally {
    // Avoid memory leaks from preloads - https://redux-toolkit.js.org/rtk-query/usage/server-side-rendering#server-side-rendering-with-nextjs
    if (store) {
      store.dispatch(seoApi.util.resetApiState())
      store.dispatch(searchApi.util.resetApiState())
      store.dispatch(translateInsightsApi.util.resetApiState())
      store.dispatch(priceInsightsApi.util.resetApiState())
      store.dispatch(trustpilotApi.util.resetApiState())
    }
  }

  return {
    props: {
      ...result.props,
      // Page props can't carry undefined values
      ...(serverPreheatRoomsParams ? {serverPreheatRoomsParams} : {})
    }
  }
}
